import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Mail, Lock, AlertCircle, Globe, Eye, EyeOff, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser, setToken } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  // Must change password state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [tempToken, setTempToken] = useState(null); // Store token for password change

  const getDefaultRouteByRole = (userData) => {
    if (userData?.is_marketplace_admin || userData?.is_super_admin) return '/marketplace-admin';
    // Marketplace-only app: qualquer outro utilizador volta ao marketplace público
    return '/';
  };

  const getRedirectAfterLogin = (userData) => {
    const fromState = location.state?.from;
    const requestedPath = typeof fromState === 'string' ? fromState : fromState?.pathname;
    if (requestedPath && requestedPath !== '/login') {
      return requestedPath;
    }
    return getDefaultRouteByRole(userData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Se já está na fase de 2FA, verificar código
    if (requires2FA) {
      try {
        const response = await axios.post(`${API}/api/auth/2fa/verify-login`, {
          email: formData.email,
          password: formData.password,
          totp_code: totpCode
        });
        
        // Login bem sucedido com 2FA
        const { access_token, user } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(user);
        navigate(getRedirectAfterLogin(user), { replace: true });
      } catch (err) {
        setError(err.response?.data?.detail || 'Código 2FA inválido');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Login normal
    const result = await login(formData.email, formData.password);

    if (result.success) {
      // Check if must change password
      if (result.mustChangePassword) {
        // Store token directly from login result (more reliable than localStorage)
        if (result.token) {
          setTempToken(result.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
        }
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      
      // Redirect based on user role
      navigate(getRedirectAfterLogin(result.user), { replace: true });
    } else if (result.requires2FA) {
      // Utilizador tem 2FA activado
      setRequires2FA(true);
      setError('');
    } else {
      // Check if account is pending approval
      if (result.error && result.error.includes('pendente')) {
        navigate('/');
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('As passwords não coincidem');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('A password deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use tempToken (from state) first, fallback to localStorage
      const token = tempToken || localStorage.getItem('token');
      
      if (!token) {
        setError('Sessão expirada. Por favor, faça login novamente.');
        setMustChangePassword(false);
        setLoading(false);
        return;
      }
      
      // Send token in BOTH header and body for maximum reliability
      await axios.post(`${API}/api/auth/force-change-password`, {
        nova_password: newPassword,
        confirmar_password: confirmPassword,
        token: token
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Password changed, now redirect
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      navigate(getRedirectAfterLogin(user), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const toggleLanguage = () => {
    setLanguage(language === 'pt' ? 'en' : 'pt');
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTotpCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
      {/* Language Toggle */}
      <button
        onClick={toggleLanguage}
        className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition"
        data-testid="language-toggle"
      >
        <Globe className="h-4 w-4" />
        {language === 'pt' ? '🇵🇹 PT' : '🇬🇧 EN'}
      </button>

      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8">
        {/* Logo */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          <img
            src="/icon-192x192.png"
            alt="Marketplace TVDE"
            className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200"
          />
          <span className="text-3xl font-bold text-slate-900">
            Marketplace <span className="text-emerald-500">TVDE</span>
          </span>
        </div>

        {/* Must Change Password Form */}
        {mustChangePassword ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Definir Nova Password</h2>
              <p className="text-gray-600 text-sm">
                A sua password é temporária. Por segurança, defina uma nova password para continuar.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Repita a password"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    A guardar...
                  </>
                ) : (
                  <>
                    <Shield className="h-5 w-5" />
                    Definir Nova Password
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-center mb-6" data-testid="login-title">
              {t('loginTitle')}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700" data-testid="login-error">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Campos normais de login */}
          {!requires2FA && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={language === 'pt' ? 'seu@email.com' : 'your@email.com'}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-green-600 hover:text-green-700"
                  data-testid="forgot-password-link"
                >
                  {language === 'pt' ? 'Esqueceu-se da password?' : 'Forgot password?'}
                </Link>
              </div>
            </>
          )}

          {/* Campo de código 2FA */}
          {requires2FA && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <Shield className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-800 mb-2">
                  {language === 'pt' ? 'Verificação de 2 Fatores' : 'Two-Factor Authentication'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'pt' 
                    ? 'Insira o código de 6 dígitos do seu autenticador (Google Authenticator/Authy)' 
                    : 'Enter the 6-digit code from your authenticator app (Google Authenticator/Authy)'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'pt' ? 'Código 2FA' : '2FA Code'}
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    data-testid="login-2fa-code-input"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleBack}
                className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm"
                data-testid="login-2fa-back-button"
              >
                ← {language === 'pt' ? 'Voltar ao login' : 'Back to login'}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (requires2FA && totpCode.length !== 6)}
            className="w-full py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            data-testid="login-submit-button"
          >
            {loading 
              ? t('loading') 
              : requires2FA 
                ? (language === 'pt' ? 'Verificar Código' : 'Verify Code')
                : t('login')
            }
          </button>
        </form>

        {!requires2FA && (
          <>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  navigate('/');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
                data-testid="login-back-link"
              >
                ← {t('back')}
              </button>
            </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
